'use client'
import type { SearchOptions } from '@giphy/js-fetch-api'
import { IChannel } from '@giphy/js-types'
import { useRouter } from 'next/navigation'
import { createContext, Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

export type ParamObject = {
    query?: string
    sort?: 'asc' | 'desc' | 'relevant' | 'trending'
    stickers?: 'ON' | 'OFF' | 'ONLY' | 'TEXT'
    videos?: 'ON' | 'OFF' | 'ONLY'
    publicSearch?: 'Yes' | 'No'
    publicTrending?: 'Yes'
    rating?: SearchOptions['rating']
    flagAndFilters?: string
    visibility?: string
    userNameFilter?: string
    validUserTypes?: string
    validRatings?: string
    channelIdFilter?: number
    searchField?: string
    queryModel?: string
    search_identity_acl?: string
    gmtOffset?: string
    since?: string
    until?: string
    integrationCompany?: string
    integration?: string
}

export type IntegrationLabels = {
    company?: string
    integration?: string
}

type CurationContextProps = {
    resetKey?: number
    params: ParamObject
    setParams: Dispatch<SetStateAction<ParamObject>>
    onParamChange: (paramObject: ParamObject) => void
    updateParams: (newParams: Partial<ParamObject>) => void
    resetParams: () => void
    currentGifCount: number
    setCurrentGifCount: Dispatch<SetStateAction<number>>
    curationLoading: boolean
    setCurationLoading: Dispatch<SetStateAction<boolean>>
    channel?: IChannel
    setChannel?: Dispatch<SetStateAction<IChannel | undefined>>
    gpsKeywords: string[]
    setGPSKeywords: (words: string[]) => void
    startDate?: string
    setStartDate: Dispatch<SetStateAction<string | undefined>>
    endDate?: string
    setEndDate: Dispatch<SetStateAction<string | undefined>>
    preLoadedParams: ParamObject
    soundOn: boolean
    setSoundOn: (on: boolean) => void
    integrationLabels: IntegrationLabels
    setIntegrationLabels: Dispatch<SetStateAction<IntegrationLabels>>
}

export const CurationContext = createContext({} as CurationContextProps)

export const CurationContextManager = ({ children }: { children: React.ReactNode }) => {
    const router = useRouter()
    const defaultParams = useMemo<ParamObject>(
        () => ({
            stickers: 'OFF',
            flagAndFilters: '[{"flag":"VERIFIED","value":true}]',
        }),
        []
    )

    const [preLoadedParams] = useState<ParamObject>(() => {
        if (typeof window !== 'undefined') {
            const search = window.location.search
            if (search && search !== '?') {
                const urlParams = new URLSearchParams(search)
                const obj: ParamObject = {}
                urlParams.forEach((value, key) => {
                    if (key === 'channelIdFilter') {
                        obj[key] = Number(value) as any
                    } else {
                        obj[key as keyof ParamObject] = value as any
                    }
                })
                if (Object.keys(obj).length === 0) {
                    obj.flagAndFilters = '[{"flag":"VERIFIED","value":true}]'
                }
                return obj
            }
        }
        return { ...defaultParams }
    })
    const [params, setParams] = useState<ParamObject>(defaultParams)
    const [currentGifCount, setCurrentGifCount] = useState<number>(0)
    const [curationLoading, setCurationLoading] = useState<boolean>(false)
    const [channel, setChannel] = useState<IChannel | undefined>()
    const [startDate, setStartDate] = useState<string>()
    const [endDate, setEndDate] = useState<string>()
    const [gpsKeywords, setGPSKeywords] = useState<string[]>([])
    const [calendarResetKey, setCalendarResetKey] = useState(0)
    const [soundOn, setSoundOn] = useState<boolean>(false)
    const [integrationLabels, setIntegrationLabels] = useState<IntegrationLabels>({})

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const search = window.location.search
            if (search && search !== '?') {
                const urlParams = new URLSearchParams(search)
                const obj: ParamObject = {}
                urlParams.forEach((value, key) => {
                    if (key === 'channelIdFilter') {
                        obj[key] = Number(value) as any
                    } else {
                        obj[key as keyof ParamObject] = value as any
                    }
                })
                if (Object.keys(obj).length === 0) {
                    obj.flagAndFilters = '[{"flag":"VERIFIED","value":true}]'
                }
                // Only update if params actually changed
                if (JSON.stringify(obj) !== JSON.stringify(params)) {
                    setParams(obj)
                }
            } else {
                setParams(defaultParams)
            }
        }
    }, [defaultParams, router])

    const resetParams = () => {
        setParams(defaultParams)
        router.replace(`?${buildQueryString(defaultParams)}`)
        setChannel?.(undefined)
        setStartDate(undefined)
        setEndDate(undefined)
        setCalendarResetKey((k) => k + 1)
        setGPSKeywords([])
        setIntegrationLabels({})
    }

    function buildQueryString(params: ParamObject) {
        return Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== '' && value !== 'undefined')
            .map(([key, value]) => {
                if (key === 'since' || key === 'until') {
                    return `${key}=${value}`
                }
                if (key === 'query') {
                    const formatted = (value as string).toLowerCase()
                    return `${encodeURIComponent(key)}=${encodeURIComponent(formatted)}`
                }
                if (key === 'flagAndFilters') {
                    return `${encodeURIComponent(key)}=${value}`
                }
                if (key === 'validUserTypes') {
                    return `${encodeURIComponent(key)}=${value}`
                }
                if (key === 'validRatings') {
                    const formatted = (value as string)
                        .split(',')
                        .map((v) => v.replace(/-/g, '').toLowerCase())
                        .join(',')
                    return `${encodeURIComponent(key)}=${formatted}`
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
            })
            .join('&')
    }

    const updateParams = (newParams: Partial<ParamObject>) => {
        setParams((prevParams) => {
            const updatedParams = { ...prevParams, ...newParams }
            const queryString = buildQueryString(updatedParams)
            router.push(`?${queryString}`)
            return Object.fromEntries(
                Object.entries(updatedParams).filter(
                    ([_, value]) => value !== undefined && value !== '' && value !== 'undefined'
                )
            ) as ParamObject
        })
    }

    // Update params and sync with URL
    const onParamChange = (newParams: ParamObject) => {
        setParams(newParams)
        const queryString = buildQueryString(newParams)
        router.push(`?${queryString}`)
    }

    return (
        <CurationContext.Provider
            value={{
                params,
                setParams,
                onParamChange,
                updateParams,
                resetParams,
                resetKey: calendarResetKey,
                currentGifCount,
                setCurrentGifCount,
                curationLoading,
                setCurationLoading,
                channel,
                setChannel,
                gpsKeywords,
                setGPSKeywords,
                startDate,
                endDate,
                setStartDate,
                setEndDate,
                preLoadedParams,
                soundOn,
                setSoundOn,
                integrationLabels,
                setIntegrationLabels,
            }}
        >
            {children}
        </CurationContext.Provider>
    )
}

export default CurationContextManager
